import { Auth, Storage } from "aws-amplify";
import { CognitoUser } from "@aws-amplify/auth";
import { CognitoUserSession } from "amazon-cognito-identity-js";

// // Manual AWS Configuration (might not be needed)
Auth.configure({
  // To get the aws credentials, you need to configure
  // the Auth module with your Cognito Federated Identity Pool
  identityPoolId: "ap-southeast-2:09a57bf1-0b6a-439a-b944-69dc6394fa95",
  region: "ap-southeast-2",
});

Storage.configure({
  AWSS3: {
    bucket: "dcm-client-v1-bucket-dev", //Your bucket name;
    region: "ap-southeast-2", //Specify the region your bucket was created in;
  },
});

export const AuthErrors = {
  UserNotConfirmedException: "UserNotConfirmedException",
  UserNotFoundException: "UserNotFoundException",
  UsernameExistsException: "UsernameExistsException",
  NotAuthorizedException: "NotAuthorizedException",
  CodeMismatchException: "CodeMismatchException",
  ExpiredCodeException: "ExpiredCodeException", //For forgot password code
  PasswordResetRequiredException: "PasswordResetRequiredException",
  NetworkError: "NetworkError",
  InvalidParameterException: "InvalidParameterException",
  LimitExceededException: "LimitExceededException",
};

export interface ReturnMessage {
  success: boolean;
  message?: string; //If it has a message, UI will display it
  nextStage?: string; //If it has a next stage, UI will redirect
}

export const STAGE = {
  signedIn: "signedIn",
  signedOut: "signedOut",
  unconfirmed: "unconfirmed",
  confirmed: "confirmed",
  forgotPasswordUnconfirmed: "forgotPasswordUnconfirmed",
  forgotPasswordConfirmed: "forgotPasswordConfirmed",
  passwordChangeRequired: "passwordChangeRequired", //Should redirect to the forgotPassword flow
  passwordChanged: "passwordChanged", //NOT USED
};

export const ERRORS = {
  network: "Network disconnected, please try again",
  userNotFound: "User with email address does not exist, sign up instead?",
  default: "Something happened, please try again",
  userAlreadyExists: "This email is already used, sign in instead?",
  codeMismatch: "Code does not match, please try again",
  wrongUsernamePassword: "Incorrect username or pasword, please try again",
  limitExceeded: "Limit exceeded, please try again later",
};

class AuthAPI {
  constructor() {}

  async signin(email: string, password: string): Promise<ReturnMessage> {
    try {
      const res = await Auth.signIn({ username: email, password });
      //If successful, res contains user + userConfirmed: false
      const {
        signInUserSession,
      }: {
        signInUserSession: CognitoUserSession;
      } = res;

      const creds = signInUserSession;
      localStorage.setItem("AWS_TOKEN", creds.getAccessToken().getJwtToken());

      return {
        success: true,
        message: "Successfully signed in",
        nextStage: STAGE.signedIn,
      };
    } catch (error) {
      //Else will return an error code
      console.log(error);

      const code = error.code;
      switch (code) {
        case AuthErrors.NetworkError:
          return {
            success: false,
            message: ERRORS.network,
          };
        case AuthErrors.UserNotConfirmedException:
          return { success: false, nextStage: STAGE.unconfirmed };
        case AuthErrors.PasswordResetRequiredException:
          return { success: false, nextStage: STAGE.passwordChangeRequired };
        case AuthErrors.UserNotFoundException:
          return {
            success: false,
            message: ERRORS.userNotFound,
          };
        case AuthErrors.NotAuthorizedException:
          return { success: false, message: ERRORS.wrongUsernamePassword };
        default:
          return {
            success: false,
            message: ERRORS.default,
          };
      }
      //ADD ERROR THAT COMES BACK when reset is required
    }
  }

  async signup(
    email: string,
    password: string,
    name: string
  ): Promise<ReturnMessage> {
    try {
      const res = await Auth.signUp({
        username: email,
        password,
        attributes: { email, name },
      });
      return { success: true, nextStage: STAGE.unconfirmed };
    } catch (error) {
      //Else will return an error code
      const code = error.code;
      switch (code) {
        case AuthErrors.NetworkError:
          return { success: false, message: ERRORS.network };
        case AuthErrors.UsernameExistsException:
          return { success: false, message: ERRORS.userAlreadyExists };
        case AuthErrors.NotAuthorizedException:
          return { success: false, message: code };
        default:
          return {
            success: false,
            message: ERRORS.default,
          };
      }
    }
  }

  async confirm(email: string, code: string): Promise<ReturnMessage> {
    try {
      const res = await Auth.confirmSignUp(email, code);
      return { success: true, nextStage: STAGE.confirmed };
    } catch (error) {
      //Else will return an error code
      const code = error.code;
      switch (code) {
        case AuthErrors.NetworkError:
          return { success: false, message: ERRORS.network };
        case AuthErrors.CodeMismatchException:
          return { success: false, message: ERRORS.codeMismatch };
        case AuthErrors.NotAuthorizedException:
          return { success: false, message: code };
        default:
          return {
            success: false,
            message: ERRORS.default,
          };
      }
    }
  }

  async resendCode(email: string): Promise<ReturnMessage> {
    try {
      const res = await Auth.resendSignUp(email);
      return { success: true, message: "Code resent" };
    } catch (error) {
      const code = error.code;
      switch (code) {
        case AuthErrors.NetworkError:
          return { success: false, message: ERRORS.network };
        default:
          return {
            success: false,
            message: ERRORS.default,
          };
      }
    }
  }

  async forgotPassword(email: string): Promise<ReturnMessage> {
    try {
      const res = await Auth.forgotPassword(email);
      console.log(res);

      return { success: true, nextStage: STAGE.forgotPasswordUnconfirmed };
    } catch (error) {
      console.log(error);
      const code = error.code;
      switch (code) {
        case AuthErrors.NetworkError:
          return { success: false, message: ERRORS.network };
        case AuthErrors.LimitExceededException:
          return { success: false, message: ERRORS.limitExceeded };
        case AuthErrors.UserNotFoundException:
          return { success: false, message: ERRORS.userNotFound };
        default:
          return {
            success: false,
            message: ERRORS.default,
          };
      }
    }
  }

  async forgotPasswordConfirm(
    email: string,
    code: string,
    password: string
  ): Promise<ReturnMessage> {
    try {
      await Auth.forgotPasswordSubmit(email, code, password);
      return { success: true, nextStage: STAGE.forgotPasswordConfirmed };
    } catch (error) {
      console.log(error);
      const code = error.code;
      switch (code) {
        case AuthErrors.NetworkError:
          return { success: false, message: ERRORS.network };
        case AuthErrors.CodeMismatchException:
          return { success: false, message: ERRORS.codeMismatch };
        case AuthErrors.UserNotFoundException:
          return { success: false, message: ERRORS.userNotFound };
        default:
          return {
            success: false,
            message: ERRORS.default,
          };
      }
    }
  }

  async signout(): Promise<ReturnMessage> {
    try {
      const res = await Auth.signOut();
      console.log(res);
      localStorage.removeItem("AWS_TOKEN");
      return { success: true, nextStage: STAGE.signedOut };
    } catch (error) {
      const code = error.code;
      switch (code) {
        case AuthErrors.NetworkError:
          return { success: false, message: ERRORS.network };
        default:
          return {
            success: false,
            message: ERRORS.default,
          };
      }
    }
  }

  async changePassword(
    oldPassword: string,
    newPassword: string
  ): Promise<ReturnMessage> {
    try {
      const user: CognitoUser = await Auth.currentAuthenticatedUser();
      if (!user) throw new Error("No current user");
      const res = await Auth.changePassword(user, oldPassword, newPassword);
      return { success: true, message: "Password has been reset" };
    } catch (error) {
      console.log(error);

      const code = error.code;
      switch (code) {
        case AuthErrors.NetworkError:
          return { success: false, message: ERRORS.network };
        default:
          return {
            success: false,
            message: ERRORS.default,
          };
      }
    }
  }

  async getToken() {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const creds = await Auth.userSession(user);
      return creds.getAccessToken().getJwtToken();
    } catch (error) {
      return null;
    }
  }

  //NOT USED
  async completeNewPassword(password: string): Promise<ReturnMessage> {
    try {
      const user:
        | CognitoUser
        | undefined = await Auth.currentAuthenticatedUser();
      console.log(user);

      await Auth.completeNewPassword(user, password, {
        // Session: await Auth.currentSession(),
      });
      return { success: true, nextStage: STAGE.passwordChanged };
    } catch (error) {
      console.log(error);

      const code = error.code;
      switch (code) {
        case AuthErrors.NetworkError:
          return { success: false, message: ERRORS.network };
        default:
          return {
            success: false,
            message: ERRORS.default,
          };
      }
    }
  }

  //Clear this later, for testing only
  async testFunction() {
    try {
      const user = await Auth.currentAuthenticatedUser();
      //not authenticated
      console.log(user);
      const creds = await Auth.currentCredentials();
      console.log(creds);
      //
      //Gets id , username, attributes
      const res = await Auth.currentUserInfo();
      console.log(res);
      //Has the token here
      const res3 = await Auth.userSession(user);
      console.log(res3);
    } catch (error) {
      console.log("error", error);
    }
  }
}
export default new AuthAPI();
